/**
 * Insights (insights-demo-01) — one account's verification history on one
 * screen: list grade, headline numbers, funnel, number-type quality, what the
 * AI caught, weekly trend and estimated hours saved.
 *
 * Totals only. The API never sends a name, phone number or file name, so the
 * page is safe to screenshot or share.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { insightsApi } from '../services/api';
import {
  CATEGORY_LABELS,
  InsightsAiCaught,
  InsightsFunnelStep,
  InsightsNumberType,
  InsightsRange,
  InsightsResponse,
  InsightsWeek,
  PhoneCategory,
} from '../types';
import {
  barPct,
  formatHours,
  formatInt,
  formatPct,
  gradeTone,
  linePoints,
  parseRange,
  RANGES,
  rememberRange,
  resolveRange,
  stackHeights,
  WEEK_SEGMENTS,
  WeekSegment,
  weekLabel,
} from '../services/insightsUtil';

// Same colour per result everywhere on the page (weekly bars, AI caught).
const CATEGORY_SERIES: Record<PhoneCategory, WeekSegment> = {
  GOOD: 'human',
  VOICEMAIL: 'voicemail',
  SWITCHBOARD: 'switchboard',
  DEAD: 'dead',
  FAX: 'dead',
  BUSY: 'other',
  NO_ANSWER: 'other',
  UNKNOWN: 'other',
};

const GRADE_RULE = 'List grade = share of live numbers: A ≥ 85%, B ≥ 75%, C ≥ 65%, D ≥ 50%, F below 50%';

export default function Insights() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlRange = searchParams.get('range');
  const range = resolveRange(urlRange);
  const [data, setData] = useState<InsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Keep the range in the URL so back/forward and reload restore it.
  useEffect(() => {
    if (urlRange !== range) setSearchParams({ range }, { replace: true });
  }, [urlRange, range, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    insightsApi
      .get(range)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your insights. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, attempt]);

  const changeRange = (value: string) => {
    const next = parseRange(value);
    if (!next) return;
    rememberRange(next);
    setSearchParams({ range: next }, { replace: true });
  };

  const empty = data !== null && !data.has_data;

  return (
    <div className={`insights${loading && data ? ' is-refreshing' : ''}`} aria-busy={loading}>
      <header className="insights-head">
        <div className="insights-title">
          <h2>Insights</h2>
          {data?.has_data && <GradeBadge grade={data.grade} livePct={data.kpis.live_pct} />}
        </div>
        <div className="insights-actions">
          <select
            className="insights-range"
            value={range}
            onChange={(e) => changeRange(e.target.value)}
            aria-label="Date range"
          >
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
          {!empty && (
            <Link to="/upload" className="btn btn-primary">Verify a new list</Link>
          )}
        </div>
      </header>

      {error && (
        <div className="alert alert-error insights-error" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-ghost" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </button>
        </div>
      )}

      {!data && loading && <Skeleton />}
      {empty && <EmptyState range={range} />}
      {data?.has_data && <Body data={data} />}
    </div>
  );
}

function Body({ data }: { data: InsightsResponse }) {
  const k = data.kpis;
  return (
    <>
      <section className="insights-kpis" aria-label="Headline numbers">
        <Kpi label="Numbers verified" value={formatInt(k.numbers_verified)} hint="Dialed and classified" />
        <Kpi label="Live numbers" value={formatInt(k.live)} pct={k.live_pct} hint="Not dead, fax or unknown" />
        <Kpi label="Humans reached" value={formatInt(k.humans)} pct={k.humans_pct} hint="A person picked up" />
        <Kpi
          label="Owners confirmed"
          value={formatInt(k.owners_confirmed)}
          pct={k.owners_pct}
          hint="Owner named on the call"
        />
        <Kpi
          label="Bad numbers removed"
          value={formatInt(k.bad_removed)}
          pct={k.bad_pct}
          hint="Dead lines and faxes"
        />
        <Kpi
          label="Hours saved"
          value={formatHours(k.hours_saved)}
          badge="est."
          hint={`est. · ${k.manual_seconds_per_dial} s per manual dial`}
          accent
        />
      </section>

      <section className="insights-row insights-row-3">
        <Panel title="List health funnel" note="share of numbers dialed">
          <Funnel steps={data.funnel} />
        </Panel>
        <Panel title="Number type quality" note="by spreadsheet column">
          <NumberTypes types={data.number_types} />
        </Panel>
        <Panel title="What the AI caught">
          <AiCaught caught={data.ai_caught} />
        </Panel>
      </section>

      <section className="insights-row insights-row-2">
        <Panel title="List quality over time" note="numbers per week">
          <WeeklyBars weeks={data.weekly} />
        </Panel>
        <Panel title="Hours saved" note="running total">
          <HoursSaved
            weeks={data.weekly}
            hours={k.hours_saved}
            workWeeks={k.work_weeks}
            secondsPerDial={k.manual_seconds_per_dial}
          />
        </Panel>
      </section>
    </>
  );
}

function GradeBadge({ grade, livePct }: { grade: string | null; livePct: number }) {
  return (
    <span className={`insights-grade tone-${gradeTone(grade)}`} title={GRADE_RULE}>
      <span className="insights-grade-letter">{grade ?? '–'}</span>
      <span className="insights-grade-text">List grade · {formatPct(livePct)} live</span>
    </span>
  );
}

function Kpi({
  label,
  value,
  pct,
  hint,
  badge,
  accent = false,
}: {
  label: string;
  value: string;
  pct?: number;
  hint: string;
  badge?: string;
  accent?: boolean;
}) {
  return (
    <div className={`insights-kpi${accent ? ' is-accent' : ''}`} title={hint}>
      <div className="insights-kpi-value">
        {value}
        {pct !== undefined && <span className="insights-kpi-pct">{formatPct(pct)}</span>}
        {badge && <span className="insights-kpi-badge">{badge}</span>}
      </div>
      <div className="insights-kpi-label">{label}</div>
      <div className="insights-kpi-hint">{hint}</div>
    </div>
  );
}

function Panel({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="insights-panel">
      <header className="insights-panel-head">
        <h3>{title}</h3>
        {note && <span className="insights-muted">{note}</span>}
      </header>
      <div className="insights-panel-body">{children}</div>
    </section>
  );
}

function Funnel({ steps }: { steps: InsightsFunnelStep[] }) {
  const dialed = steps[0]?.count ?? 0;
  return (
    <ol className="insights-funnel">
      {steps.map((s) => (
        <li
          key={s.key}
          title={`${s.label}: ${formatInt(s.count)} of ${formatInt(dialed)} numbers dialed (${formatPct(s.pct)})`}
        >
          <span className="insights-funnel-label">{s.label}</span>
          <span className="insights-bar-track">
            <span className={`insights-bar-fill funnel-${s.key}`} style={{ width: `${barPct(s.count, dialed)}%` }} />
          </span>
          <span className="insights-num">{formatInt(s.count)}</span>
          <span className="insights-num insights-num-soft">{formatPct(s.pct)}</span>
        </li>
      ))}
    </ol>
  );
}

function NumberTypes({ types }: { types: InsightsNumberType[] }) {
  const most = (pick: (t: InsightsNumberType) => number) => Math.max(0, ...types.map(pick));
  const maxHuman = most((t) => t.human_pct);
  const maxOwner = most((t) => t.owner_pct);
  const maxBad = most((t) => t.bad_pct);
  return (
    <div className="insights-types">
      {types.map((t) => (
        <div key={t.group} className="insights-type">
          <div className="insights-type-head">
            <span>{t.group}</span>
            <span className="insights-muted">
              {formatInt(t.count)} numbers · {formatPct(t.share_pct)}
            </span>
          </div>
          <Metric label="Human" pct={t.human_pct} max={maxHuman} series="human" best={t.count > 0 && t.human_pct === maxHuman} group={t.group} />
          <Metric label="Owner" pct={t.owner_pct} max={maxOwner} series="owner" best={t.count > 0 && t.owner_pct === maxOwner} group={t.group} />
          <Metric label="Bad" pct={t.bad_pct} max={maxBad} series="dead" best={false} group={t.group} />
        </div>
      ))}
    </div>
  );
}

function Metric({
  label,
  pct,
  max,
  series,
  best,
  group,
}: {
  label: string;
  pct: number;
  max: number;
  series: WeekSegment | 'owner';
  best: boolean;
  group: string;
}) {
  return (
    <div className="insights-metric" title={`${group}: ${formatPct(pct)} ${label.toLowerCase()}`}>
      <span className="insights-metric-label">{label}</span>
      <span className="insights-bar-track">
        <span className={`insights-bar-fill series-${series}`} style={{ width: `${barPct(pct, max)}%` }} />
      </span>
      <span className="insights-num">{formatPct(pct)}</span>
      <span className="insights-best">{best && max > 0 ? 'best' : ''}</span>
    </div>
  );
}

function AiCaught({ caught }: { caught: InsightsAiCaught }) {
  const entries = (Object.entries(caught.by_category) as [PhoneCategory, number][]).filter(([, n]) => n > 0);
  const max = Math.max(0, ...entries.map(([, n]) => n));
  if (caught.total === 0) {
    return <p className="insights-muted">Every call in this period was clear to the carrier — nothing needed the AI.</p>;
  }
  return (
    <div className="insights-ai">
      <div className="insights-big">
        <strong>{formatInt(caught.total)}</strong>
        <span>calls · {formatPct(caught.pct)} of all</span>
      </div>
      <p className="insights-muted insights-ai-caption">
        The carrier couldn't tell what answered. The AI listened to each one and sorted it into:
      </p>
      <ul className="insights-ai-list">
        {entries.map(([cat, n]) => (
          <li key={cat} title={`${CATEGORY_LABELS[cat]}: ${formatInt(n)} calls`}>
            <span className="insights-ai-label">{CATEGORY_LABELS[cat]}</span>
            <span className="insights-bar-track">
              <span className={`insights-bar-fill series-${CATEGORY_SERIES[cat]}`} style={{ width: `${barPct(n, max)}%` }} />
            </span>
            <span className="insights-num">{formatInt(n)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function weekTitle(w: InsightsWeek): string {
  const parts = WEEK_SEGMENTS.filter((s) => w[s.key] > 0).map((s) => `${s.label.toLowerCase()} ${formatInt(w[s.key])}`);
  return `Week of ${weekLabel(w.week_start)}: ${formatInt(w.total)} numbers${parts.length ? ` — ${parts.join(', ')}` : ''}`;
}

function WeeklyBars({ weeks }: { weeks: InsightsWeek[] }) {
  const tallest = Math.max(0, ...weeks.map((w) => w.total));
  // At most ~10 labels, so a long "All time" range stays readable.
  const labelEvery = Math.max(1, Math.ceil(weeks.length / 10));
  return (
    <div className="insights-weekly">
      <div className="insights-legend">
        {WEEK_SEGMENTS.map((s) => (
          <span key={s.key}>
            <i className={`series-${s.key}`} />
            {s.label}
          </span>
        ))}
      </div>
      <div className="insights-weekly-bars">
        {weeks.map((w, i) => (
          <div key={w.week_start} className="insights-week" title={weekTitle(w)}>
            <div className="insights-week-stack">
              {stackHeights(w, tallest)
                .filter((s) => s.count > 0)
                .map((s) => (
                  <span key={s.key} className={`series-${s.key}`} style={{ flexBasis: `${s.pct}%` }} />
                ))}
            </div>
            <span className="insights-week-label">{i % labelEvery === 0 ? weekLabel(w.week_start) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoursSaved({
  weeks,
  hours,
  workWeeks,
  secondsPerDial,
}: {
  weeks: InsightsWeek[];
  hours: number;
  workWeeks: number;
  secondsPerDial: number;
}) {
  const points = linePoints(weeks.map((w) => w.hours_saved_cumulative));
  return (
    <div className="insights-hours">
      <div className="insights-big">
        <strong>{formatHours(hours)}</strong>
        <span>≈ {workWeeks} work-weeks</span>
      </div>
      <div className="insights-hours-chart">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <polygon className="insights-hours-area" points={`0,100 ${points} 100,100`} />
          <polyline className="insights-hours-line" points={points} vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="insights-hours-hover">
          {weeks.map((w) => (
            <span
              key={w.week_start}
              title={`By the week of ${weekLabel(w.week_start)}: ${formatHours(w.hours_saved_cumulative)} saved`}
            />
          ))}
        </div>
      </div>
      {weeks.length > 0 && (
        <div className="insights-hours-axis">
          <span>{weekLabel(weeks[0].week_start)}</span>
          <span>{weekLabel(weeks[weeks.length - 1].week_start)}</span>
        </div>
      )}
      <p className="insights-muted insights-hours-note">est. · {secondsPerDial} s per manual dial (dial, ring, listen, log)</p>
    </div>
  );
}

function Skeleton() {
  return (
    <>
      <div className="insights-kpis">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="insights-kpi insights-skel" />
        ))}
      </div>
      <div className="insights-row insights-row-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="insights-panel insights-skel" />
        ))}
      </div>
      <div className="insights-row insights-row-2">
        {[0, 1].map((i) => (
          <div key={i} className="insights-panel insights-skel" />
        ))}
      </div>
    </>
  );
}

function EmptyState({ range }: { range: InsightsRange }) {
  const everything = range === 'all';
  return (
    <div className="card insights-empty">
      <h3>{everything ? 'Verify your first list to see insights' : 'No numbers verified in this period'}</h3>
      <p className="insights-muted">
        {everything
          ? 'Upload a spreadsheet of phone numbers and Dial Sniper will dial, sort and score it for you.'
          : 'Pick a longer date range above, or verify a new list.'}
      </p>
      <Link to="/upload" className="btn btn-primary">Verify a new list</Link>
    </div>
  );
}
